import React, { useEffect, useState, useCallback } from 'react';
import { Pulse, MagnifyingGlass, MapPin, Buildings, ArrowsCounterClockwise, Warning, Pause, Play } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Live quota widget for external scan-booster sources (Brave / Google Places / KvK).
 * Renders compact progress bars + per-profile breakdown.
 * Shows ALL profiles (incl. 0-usage) and lets the user pause profiles so the
 * monthly quota flows entirely to active profiles.
 *
 * Backend: GET /api/system/scan-sources-status (combined stats + roster).
 */
function ProgressBar({ used, total, color = 'emerald' }) {
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const colorMap = {
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
    rose: 'bg-rose-500',
    blue: 'bg-blue-500',
  };
  const fill = pct >= 90 ? 'bg-rose-500' : pct >= 70 ? 'bg-amber-500' : colorMap[color];
  return (
    <div className="w-full h-1.5 bg-zinc-100 rounded-full overflow-hidden">
      <div className={`h-full ${fill} transition-all duration-300`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function SourceCard({ icon: Icon, title, configured, used, total, period, perProfile, profileRoster, onTogglePause, accent, extra }) {
  if (!configured) {
    return (
      <div className="bg-white p-4 border border-dashed border-zinc-200 rounded-md" data-testid={`quota-card-${title.toLowerCase()}`}>
        <div className="flex items-center gap-2 mb-2">
          <Icon size={14} className="text-zinc-400" />
          <span className="text-xs font-medium text-zinc-700">{title}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-500 ml-auto">niet geconfigureerd</span>
        </div>
        <div className="text-[10px] text-zinc-400">{extra || 'Niet actief — voeg API key toe in backend/.env'}</div>
      </div>
    );
  }
  const remaining = Math.max(0, total - used);
  const pct = total > 0 ? Math.round((used / total) * 100) : 0;
  // Merge roster (all profiles) with per-profile usage so newly-created profiles
  // show up at 0 instead of being invisible until first call.
  const usedMap = perProfile || {};
  const rows = (profileRoster || []).map(p => ({
    slug: p.slug, name: p.name || p.slug,
    is_paused: !!p.is_paused,
    used: Number(usedMap[p.slug] || 0),
  })).sort((a, b) => {
    if (a.is_paused !== b.is_paused) return a.is_paused ? 1 : -1;  // paused last
    return b.used - a.used;
  });
  return (
    <div className="bg-white p-4 border border-zinc-200 rounded-md" data-testid={`quota-card-${title.toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={accent || 'text-emerald-600'} />
        <span className="text-xs font-medium text-zinc-700">{title}</span>
        <span className="text-[10px] text-zinc-400 ml-auto">{period}</span>
      </div>
      <div className="flex items-baseline gap-1 mb-1">
        <span className="font-data text-xl font-bold">{used.toLocaleString()}</span>
        <span className="text-zinc-400 text-xs">/ {total.toLocaleString()} ({pct}%)</span>
      </div>
      <ProgressBar used={used} total={total} />
      <div className="text-[10px] text-zinc-500 mt-1.5">
        <strong>{remaining.toLocaleString()}</strong> nog beschikbaar
      </div>
      {rows.length > 0 && (
        <div className="mt-2 pt-2 border-t border-zinc-100 space-y-1" data-testid={`quota-profiles-${title.toLowerCase()}`}>
          {rows.map(({ slug, name, is_paused, used: u }) => (
            <div key={slug} className={`flex items-center justify-between gap-2 text-[10px] ${is_paused ? 'opacity-50' : ''}`}>
              <span className={`truncate flex-1 ${is_paused ? 'line-through' : 'text-zinc-600'}`} title={slug}>{name}</span>
              <span className="font-data text-zinc-700">{u}</span>
              {onTogglePause && (
                <button
                  type="button"
                  data-testid={`pause-toggle-${slug}`}
                  onClick={() => onTogglePause(slug, !is_paused)}
                  title={is_paused ? 'Activeer profiel — quota stroomt weer naar dit profiel' : 'Pauzeer profiel — quota wordt vrijgegeven voor andere profielen'}
                  className={`p-0.5 rounded-sm ${is_paused ? 'text-zinc-400 hover:text-emerald-600' : 'text-zinc-400 hover:text-amber-600'}`}
                >
                  {is_paused ? <Play size={9} weight="fill" /> : <Pause size={9} weight="fill" />}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {extra && (
        <div className="text-[10px] text-amber-600 mt-1.5 flex items-center gap-1">
          <Warning size={10} /> {extra}
        </div>
      )}
    </div>
  );
}

export default function ScanSourcesWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await fetch(`${API}/system/scan-sources-status`, { credentials: 'include' });
      const d = await r.json();
      setData(d);
    } catch {
      // ignore
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // refresh every 60s
    return () => clearInterval(t);
  }, [load]);

  const togglePause = useCallback(async (slug, paused) => {
    try {
      const r = await fetch(`${API}/profiles/${slug}/pause`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_paused: paused }),
      });
      if (r.ok) {
        await load();
      } else {
        const d = await r.json().catch(() => ({}));
        window.alert(d.detail || 'Pauze-status kon niet worden gewijzigd');
      }
    } catch {
      window.alert('Netwerkfout');
    }
  }, [load]);

  if (loading) {
    return (
      <div className="bg-white p-4 border border-zinc-200 rounded-md mb-6">
        <div className="text-xs text-zinc-400">Quota status laden...</div>
      </div>
    );
  }
  if (!data) return null;

  const brave = data.brave_search || {};
  const places = data.google_places || {};
  const cse = data.google_cse || {};
  const kvk = data.kvk || {};
  const roster = data.profiles || [];

  return (
    <div className="mb-8" data-testid="scan-sources-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Pulse size={16} className="text-emerald-600" />
          <h2 className="font-heading text-lg font-semibold">Externe Scan Bronnen</h2>
          <span className="text-xs text-zinc-500">— quota verdeeld over actieve profielen</span>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          className="text-xs text-zinc-500 hover:text-zinc-800 flex items-center gap-1"
          data-testid="quota-refresh-btn"
        >
          <ArrowsCounterClockwise size={11} className={refreshing ? 'animate-spin' : ''} /> Verversen
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <SourceCard
          icon={MagnifyingGlass}
          title="Brave Search"
          configured={!!brave.configured}
          used={brave.quota_used_this_month || 0}
          total={brave.quota_monthly_total || 0}
          perProfile={brave.per_profile_used}
          profileRoster={roster}
          onTogglePause={togglePause}
          period="deze maand"
          accent="text-amber-600"
          extra={brave.disabled_reason ? `Geblokkeerd: ${brave.disabled_reason}` : null}
        />
        <SourceCard
          icon={MapPin}
          title="Google Places"
          configured={!!places.configured}
          used={places.quota_used_this_month || 0}
          total={places.quota_monthly_total || 0}
          perProfile={places.per_profile_used}
          profileRoster={roster}
          onTogglePause={togglePause}
          period="deze maand"
          accent="text-blue-600"
          extra={places.disabled_reason ? `Geblokkeerd: ${places.disabled_reason}` : null}
        />
        <SourceCard
          icon={MagnifyingGlass}
          title="Google CSE"
          configured={!!cse.configured}
          used={cse.quota_used_today || 0}
          total={cse.quota_daily_total || 0}
          perProfile={cse.per_profile_used}
          profileRoster={roster}
          onTogglePause={togglePause}
          period="vandaag"
          accent="text-cyan-600"
          extra={cse.disabled_reason ? `Geblokkeerd: ${cse.disabled_reason}` : 'Google CSE is closed voor nieuwe accounts'}
        />
        <SourceCard
          icon={Buildings}
          title="KvK NL"
          configured={!!kvk.configured}
          used={0}
          total={0}
          perProfile={null}
          profileRoster={null}
          period={kvk.environment === 'prod' ? 'productie' : 'test-env'}
          accent="text-violet-600"
          extra={kvk.environment === 'test' ? 'Test-env levert demo data' : null}
        />
      </div>
    </div>
  );
}
